import Config from "@/common/config";
import { baseAxiosInstance } from "@/api/base";
import type { AxiosResponse } from "axios";
import { toRelative } from "@/common/helpers";
import type { RegistrationUser } from "@/common/types/user";

const authEndpoint = Config.apiEndpoints.auth;
const defaultEndpoint = Config.apiEndpoints.default;

const loginEndpoint = toRelative(authEndpoint + "/login/");
const logoutEndpoint = toRelative(authEndpoint + "/logout/");
const tokenValidEndpoint = toRelative(defaultEndpoint + "/token-validity/");
const registrationEndpoint = toRelative(authEndpoint + "/registration/");
const resetPasswordEndpoint = toRelative(defaultEndpoint + "/reset-password/");
const acceptTermsOfUseEndpoint = toRelative(
  defaultEndpoint + "/accept-terms-of-service/",
);
const acceptPrivacyPolicyEndpoint = toRelative(
  defaultEndpoint + "/accept-privacy-policy/",
);
const forgotPasswordEndpoint = toRelative(
  authEndpoint + "/send-password-forgot-email/",
);
const confirmEmailEndpoint = toRelative(defaultEndpoint + "/confirm-email/");
const confirmEmailUpdateEndpoint = toRelative(
  defaultEndpoint + "/confirm-email-update/",
);
const resetApiKeyEndpoint = toRelative(defaultEndpoint + "/reset-api-token");

const otpRequestEndpoint = toRelative(defaultEndpoint + "/otp/request");
const otpVerifyEndpoint = toRelative(defaultEndpoint + "/otp/verify");

/**
 * @summary Authenticates the user
 * @param username The username or email of the user to authenticate.
 * @param password The password provided to authenticate the user.
 * @param otp One-Time-Password if the user has MFA enabled
 */
async function login(
  username: string,
  password: string,
  otp?: string,
): Promise<AxiosResponse> {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formData = new FormData();
  formData.append("username", username);
  formData.append("password", password);
  formData.append("userTimezone", timeZone);
  if (otp) {
    formData.append("otp", otp);
  }

  const config = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  };

  return await baseAxiosInstance.post(loginEndpoint, formData, config);
}

/**
 * @summary Terminates the user session
 */
async function logout(token?: string): Promise<AxiosResponse> {
  return await baseAxiosInstance.post(
    logoutEndpoint,
    null,
    configFromOptionalToken(token),
  );
}

/**
 * @summary Checks if the token is valid
 */
async function tokenValid(token?: string): Promise<AxiosResponse> {
  return await baseAxiosInstance.post(
    tokenValidEndpoint,
    null,
    configFromOptionalToken(token),
  );
}

async function resetApiKey() {
  return await baseAxiosInstance.put(resetApiKeyEndpoint);
}

/**
 * @summary Registers a new user
 * @param user The user to register.
 */
async function register(user: RegistrationUser): Promise<AxiosResponse> {
  return await baseAxiosInstance.post(registrationEndpoint, user);
}

/**
 * @summary Sets the new password for the user specified in the Authorization header token
 * @param password The new password.
 * @param token Optional token to use in the Authorization header of this request
 */
async function resetPassword(
  password: string,
  token?: string,
): Promise<AxiosResponse> {
  return await baseAxiosInstance.put(
    resetPasswordEndpoint,
    {
      value: password,
    },
    configFromOptionalToken(token),
  );
}

/**
 * @summary Sends an email to reset the password
 * @param email The email address to which to send the mail
 */
async function forgotPassword(email: string): Promise<AxiosResponse> {
  return await baseAxiosInstance.put(forgotPasswordEndpoint, {
    email: email,
  });
}

/**
 * @summary Accepts the terms of use for the user specified in the Authorization header token
 * @param token Optional token to use in the Authorization header of this request
 */
async function acceptTermsOfUse(token?: string): Promise<AxiosResponse> {
  return await baseAxiosInstance.put(
    acceptTermsOfUseEndpoint,
    null,
    configFromOptionalToken(token),
  );
}

/**
 * @summary Accepts the privacy policy for the user specified in the Authorization header token
 * @param token Optional token to use in the Authorization header of this request
 */
async function acceptPrivacyPolicy(token?: string): Promise<AxiosResponse> {
  return await baseAxiosInstance.put(
    acceptPrivacyPolicyEndpoint,
    null,
    configFromOptionalToken(token),
  );
}

/**
 * @summary Confirms the email for the user specified in the Authorization header token
 * @param token Optional token to use in the Authorization header of this request
 */
async function confirmEmail(token?: string): Promise<AxiosResponse> {
  return await baseAxiosInstance.put(
    confirmEmailEndpoint,
    null,
    configFromOptionalToken(token),
  );
}

/**
 * @summary Confirms the updated email for the user specified in the Authorization header token
 * @param token Optional token to use in the Authorization header of this request
 * @param check Check param to validate the authenticity of the email
 */
async function confirmEmailUpdate(
  token?: string,
  check?: string,
): Promise<AxiosResponse> {
  console.log("confirmEmailUpdate: " + token);
  console.log("confirmEmailUpdate check: " + check);

  return await baseAxiosInstance.put(
    confirmEmailUpdateEndpoint,
    {
      check: check,
    },
    configFromOptionalToken(token),
  );
}

const configFromOptionalToken = (token?: string) =>
  token !== undefined
    ? {
        headers: {
          Authorization: token.startsWith("Bearer") ? token : `Bearer ${token}`,
        },
      }
    : {};

/**
 * @summary Triggers sending a new OTP to a user via email
 * @param token Optional token to use in the Authorization header of this request
 */
async function requestOtp(token?: string): Promise<AxiosResponse> {
  return await baseAxiosInstance.post(
    otpRequestEndpoint,
    null,
    configFromOptionalToken(token),
  );
}

/**
 * @summary Verifies that a user-provided OTP is correct
 * @param otp The one-time password entered by the user
 * @param token Optional token to use in the Authorization header of this request
 */
async function verifyOtp(otp: string, token?: string): Promise<AxiosResponse> {
  return await baseAxiosInstance.post(
    otpVerifyEndpoint,
    { otp },
    configFromOptionalToken(token),
  );
}

export const authAPI = {
  login,
  logout,
  register,
  resetPassword,
  forgotPassword,
  acceptTermsOfUse,
  acceptPrivacyPolicy,
  confirmEmail,
  confirmEmailUpdate,
  tokenValid,
  resetApiToken: resetApiKey,
  requestOtp,
  verifyOtp,
};
